import React, { useState } from 'react';
import { Sofa, BedDouble, Check, CalendarCheck } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import BookingModal from './BookingModal';

const ServicesSection = ({ t, currentLang }) => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      icon: Sofa,
      title: t.services.couch.title,
      description: t.services.couch.description,
      features: t.services.couch.features,
      color: '#2196f3',
      bgColor: 'bg-[#2196f3]/10',
      borderColor: 'border-[#2196f3]/20'
    },
    {
      icon: BedDouble,
      title: t.services.mattress.title,
      description: t.services.mattress.description,
      features: t.services.mattress.features,
      color: '#5cd26f',
      bgColor: 'bg-[#5cd26f]/10',
      borderColor: 'border-[#5cd26f]/20'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0d2e5d] mb-4">
            {t.services.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 border-2 overflow-hidden"
              >
                <CardContent className="p-8">
                  {/* Icon */}
                  <div
                    className={`inline-flex p-4 ${service.bgColor} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <IconComponent
                      className="w-8 h-8"
                      style={{ color: service.color }}
                    />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-bold text-[#0d2e5d] mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div
                          className={`flex-shrink-0 w-5 h-5 ${service.bgColor} rounded-full flex items-center justify-center mt-0.5`}
                        >
                          <Check
                            className="w-3 h-3"
                            style={{ color: service.color }}
                          />
                        </div>
                        <span className="text-gray-700 text-sm">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Buttons */}
                  <div className="space-y-2">
                    <Button
                      onClick={() => setIsBookingOpen(true)}
                      className="w-full bg-[#5cd26f] hover:bg-[#4ab860] text-white"
                    >
                      <CalendarCheck className="w-4 h-4 mr-2" />
                      {currentLang === 'ro' ? 'Solicită Programare' : 'Book Appointment'}
                    </Button>
                    <Button
                      onClick={scrollToContact}
                      variant="outline"
                      className="w-full"
                      style={{ borderColor: service.color, color: service.color }}
                    >
                      {t.common.requestQuote}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            {t.contact.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setIsBookingOpen(true)}
              size="lg"
              className="bg-[#5cd26f] hover:bg-[#4ab860] text-white px-8"
            >
              <CalendarCheck className="w-5 h-5 mr-2" />
              {currentLang === 'ro' ? 'Solicită Programare' : 'Book Appointment'}
            </Button>
            <Button
              onClick={scrollToContact}
              size="lg"
              variant="outline"
              className="border-2 border-[#2196f3] text-[#2196f3] hover:bg-[#2196f3] hover:text-white px-8"
            >
              {t.common.requestQuote}
            </Button>
          </div>
        </div>

        {/* Booking Modal */}
        <BookingModal
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
          t={t}
          currentLang={currentLang}
        />
      </div>
    </section>
  );
};

export default ServicesSection;
